import {QUERY_DATE_OFFSET_REGEX, QUERY_DATE_TOKENS, QUERY_DATE_UNITS, QUERY_TOKEN_PREFIX} from '../constants/queries'

const STATE_REASON_REGEX = /(?:^|\s)reason:"?(completed|not(\s|-)planned)"?(?:$|\s)/g

export type ScopedRepository = {owner: string; name: string; is_archived?: boolean}

export type ItemsType = 'issues' | 'pullRequests' | 'any'

// Takes in the user supplied query string,
// removes some duplicate and ignored tokens,
// and returns the modified query string.
//   ex: getQuery(query: "is:issue purple type:issue", scopedRepository?: true) =>
//   "is:issue purple repo:owner/repo sort:sort:created-desc"
// Warning, this function must stay in sync with its counterpart in the backend
// see app/helpers/issues_react_helper.rb#get_repo_query
export function getQuery(query: string, scopedRepository?: ScopedRepository, bestMatchSortEnabled?: boolean): string {
  const queryStringWithDates = replaceDateTokens(applyQueryDefaults(query, scopedRepository?.is_archived || false))

  if (!scopedRepository) {
    return queryStringWithDates
  }

  let cleanQuery = ''
  const parsedQuery = parseQuery(queryStringWithDates)
  const deduplicatedQuery = removeDuplicateValues(parsedQuery)
  const defaultSort = bestMatchSortEnabled ? 'sort:relevance-desc' : 'sort:created-desc'
  const sortQualifier = parsedQuery.has('sort') ? '' : defaultSort

  cleanQuery = queryStringWithDates
  const removedValues = mapDifference(parsedQuery, deduplicatedQuery)
  for (const [key, value] of removedValues) {
    const str = `${key}:${value}`
    cleanQuery = cleanQuery.replace(str, '')
  }
  cleanQuery = removeIgnoredQualifiers(cleanQuery)
  // Replace any whitespace with a single whitespace except for the filter values
  cleanQuery = cleanQuery
    .replace(/("[^"]*")|[ \t\r\n]+/g, (_, quoted) => {
      return quoted ? quoted : ' '
    })
    .trim()

  cleanQuery = cleanQuery.trim()
  const scopedRepoQualifier = `repo:${scopedRepository.owner}/${scopedRepository.name}`
  return [cleanQuery, scopedRepoQualifier, sortQualifier].join(' ').trim()
}

// Applying empty query logic - `archived:false`
export function applyQueryDefaults(query: string, isRepoArchived: boolean) {
  // If the repo is archived, we don't want to filter by archived:false since all the issues are archived
  if (isRepoArchived) {
    return query
  }

  return `${query === '' ? 'archived:false' : query}`
}

export function replaceDateTokens(query: string) {
  const tokenPositions = findTokenPositions(query)
  if (tokenPositions.length === 0) {
    return query
  }
  const replacements = findTokenReplacements(query, tokenPositions)
  if (replacements.length === 0) {
    return query
  }

  return replaceTokens(query, replacements)
}

// Qualifiers where the backend search service does not support comma-separated values.
// The filter UI joins multiple selections with commas (e.g. repo:a,b), but the backend
// expects separate qualifiers joined with OR (repo:a OR repo:b).
// With advanced search, space-separated repo/org/user qualifiers use AND semantics,
// so we must use explicit OR to get the expected union behavior.
const EXPANDABLE_QUALIFIER_REGEX = /^(-?)(repo|org|user):(.+)/i

/**
 * Expands comma-separated values for qualifiers that the backend search service
 * does not support with comma syntax (repo, org, user).
 * Uses OR between expanded values because advanced search treats spaces as AND.
 *
 * @example
 * expandCommaSeparatedQualifiers("repo:github/foo,github/bar is:issue")
 * // => "(repo:github/foo OR repo:github/bar) is:issue"
 *
 * expandCommaSeparatedQualifiers("user:alice,bob")
 * // => "(user:alice OR user:bob)"
 *
 * expandCommaSeparatedQualifiers("-repo:github/foo,github/bar")
 * // => "-repo:github/foo -repo:github/bar"
 */
export function expandCommaSeparatedQualifiers(query: string): string {
  // Split by whitespace, preserving quoted strings
  const tokens = query.match(/((?:[^\s"]+|"[^"]*")+)/g)
  if (!tokens) return query

  const expanded: string[] = []

  for (const token of tokens) {
    const match = token.match(EXPANDABLE_QUALIFIER_REGEX)
    if (!match) {
      expanded.push(token)
      continue
    }

    const [, negation, qualifier, value] = match

    // Don't expand if the entire value is quoted
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (value!.startsWith('"') && value!.endsWith('"')) {
      expanded.push(token)
      continue
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const values = value!.split(',').filter(v => v.length > 0)
    if (values.length === 1) {
      expanded.push(token)
    } else if (negation) {
      // Negated qualifiers: -repo:a,b => -repo:a -repo:b (AND semantics is correct for exclusion)
      for (const v of values) {
        expanded.push(`${negation}${qualifier}:${v}`)
      }
    } else {
      // Positive qualifiers: repo:a,b => (repo:a OR repo:b)
      const orGroup = values.map(v => `${qualifier}:${v}`).join(' OR ')
      expanded.push(`(${orGroup})`)
    }
  }

  return expanded.join(' ')
}

export function checkIfStateReasonPresent(query: string) {
  return query.match(STATE_REASON_REGEX)
}

type tokenPosition = {
  token: string
  pos: number
}

type replacement = {
  newValue: string
  length: number
}

type tokenReplacement = {
  tokenPosition: tokenPosition
  replacement: replacement
}

// Finds all date tokens in the query, and returns their positions
function findTokenPositions(query: string) {
  const tokenPositions: tokenPosition[] = []
  let pos = query.indexOf(QUERY_TOKEN_PREFIX)
  if (pos === -1) {
    return tokenPositions
  }
  while (pos < query.length) {
    for (const tokenName in QUERY_DATE_TOKENS) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const token = QUERY_DATE_TOKENS[tokenName]!
      if (query.startsWith(token, pos)) {
        tokenPositions.push({token, pos})
        pos += token.length
        break
      }
    }
    pos = query.indexOf(QUERY_TOKEN_PREFIX, pos + 1)
    if (pos === -1) {
      break
    }
  }

  return tokenPositions
}

function tokenToDate(now: Date, token: string) {
  if (token === QUERY_DATE_TOKENS['today']) {
    return now.getTime()
  } else {
    // Currently only @today is supported
    return undefined
  }
}

function calculateNewDate(now: Date, value: number, unit: string, op: string) {
  const newDate = new Date(now.getTime())

  const sign = op === '-' ? -1 : 1
  switch (unit) {
    case QUERY_DATE_UNITS.day:
      newDate.setDate(now.getDate() + sign * value)
      break
    case QUERY_DATE_UNITS.week:
      newDate.setDate(now.getDate() + sign * value * 7)
      break
    case QUERY_DATE_UNITS.month:
      newDate.setMonth(now.getMonth() + sign * value)
      // This handles a descrepancy between how the Date implementation in js and ruby handle addition/subtraction of months
      // In JS: 2023-05-31 - 1 month ==> 2023-05-01
      // In rb: 2023-05-31 - 1 month ==> 2023-04-30
      if (newDate.getDate() === 1 && now.getDate() !== 1) {
        newDate.setDate(newDate.getDate() - 1)
      }
      break
    case QUERY_DATE_UNITS.year:
      newDate.setFullYear(now.getFullYear() + sign * value)
      break
    default:
      return null // Invalid unit
  }
  return newDate
}

function calculateDateWithOffset(regexResult: RegExpExecArray | null, date: Date, op: string, length: number) {
  if (!regexResult) {
    return null // Invalid offset modifer
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const value = parseInt(regexResult[1]!, 10)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const unit = regexResult[2]!

  length += op.length + regexResult[0].length

  const dateWithOffset = calculateNewDate(date, value, unit, op)
  if (dateWithOffset) {
    return {newValue: dateWithOffset.toISOString().substring(0, 10), length}
  }
  // Invalid relative value
  return null
}

function calculateReplacement(query: string, tokenPosition: tokenPosition, date: Date): replacement | null {
  const tokenLength = tokenPosition.token.length

  const op = query.substring(tokenPosition.pos + tokenLength, tokenPosition.pos + tokenLength + 1)

  if (op === '' || op === ' ') {
    return {newValue: date.toISOString().substring(0, 10), length: tokenLength}
  }
  if (op === '-' || op === '+') {
    const rest = query.substring(tokenPosition.pos + tokenPosition.token.length + op.length)
    const result = QUERY_DATE_OFFSET_REGEX.exec(rest)
    return calculateDateWithOffset(result, date, op, tokenLength)
  }
  // Invalid op
  return null
}

// For each date token in the query, returns the replacement value and the length of the string to replace
function findTokenReplacements(query: string, tokenPositions: tokenPosition[]) {
  const now = new Date()

  const replacements: tokenReplacement[] = []
  for (const tokenPosition of tokenPositions) {
    const newDateTime = tokenToDate(now, tokenPosition.token)
    if (!newDateTime) {
      continue
    }
    const replacement = calculateReplacement(query, tokenPosition, new Date(newDateTime))
    if (!replacement) {
      continue
    }
    replacements.push({tokenPosition, replacement})
  }

  return replacements
}

function replaceTokens(query: string, replacements: tokenReplacement[]) {
  let newQuery = query
  for (const tokenReplacement of replacements.reverse()) {
    newQuery =
      newQuery.substring(0, tokenReplacement.tokenPosition.pos) +
      tokenReplacement.replacement.newValue +
      newQuery.substring(tokenReplacement.tokenPosition.pos + tokenReplacement.replacement.length)
  }
  return newQuery
}

/**
 * Parse the string query into a map of search tokens
 * Values that don't contain a colon are treated as a search term and stored with the `searchTerms` key
 *
 * @param query GitHub search query
 * @returns Map of search tokens + search terms
 * @example
 * const queryMap = parseQuery('repo:github/github')
 * {
 *  repo: ['github.com'],
 *  searchTerms: []
 * }
 *
 * const queryMap = parseQuery('repo:github/github repo:github/issues state:open label:bug fun issue')
 * {
 *  repo: ['github/github', 'github/issues'],
 *  state: ['open'],
 *  label: ['bug']
 *  searchTerms: ['fun', 'issue']
 * }
 */
export function parseQuery(query: string): Map<string, string[]> {
  const queryMap: Map<string, string[]> = new Map()
  queryMap.set('searchTerms', [])

  const matchBySpaceIgnoreQuotedExp = /((?:[^\s"]+|"[^"]*")+)/g
  const tokens = query
    .trim()
    .split(matchBySpaceIgnoreQuotedExp)
    .filter(item => item.trim() !== '') // Remove duds

  for (const token of tokens) {
    const [key, value] = token.split(/:(.*)/s) // split on first occurence of : in token

    // Contains ':' so it's a filter token
    if (key && value) {
      if (!queryMap.has(key)) {
        queryMap.set(key, [value])
        continue
      }

      // We can have more than one usage of the filter, so we append if the key exists
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      queryMap.set(key, [...queryMap.get(key)!, value])
      continue
    }

    // Values without ':' are considered search terms
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryMap.set('searchTerms', [...queryMap.get('searchTerms')!, token])
  }

  return queryMap
}

export function findSortReactionInQuery(query: string): boolean {
  if (!query) return false
  const parsedQuery = parseQuery(query)
  const sort = parsedQuery.get('sort')

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (sort && sort.length === 1 && sort[0]!.startsWith('reactions-')) || false
}

export function replaceSortInQuery(query: string, textToReplace: string): string {
  // replace current sort:{value} in query with the selected one
  return query
    .replace(/\bsort\S+/gi, '')
    .concat(` ${textToReplace}`)
    .replaceAll('  ', ' ')
}

export function checkIfQuerySupportsPr(query: string) {
  const parsedQuery = parseQuery(query)
  const searchTerms = new Set([...(parsedQuery.get('is') || []), ...(parsedQuery.get('type') || [])])
  // Query contains PR search terms if it doesn't specify the type, or it does not include the type issue,
  // or if it specifies the type as PR or pull-request
  return searchTerms.size === 0 || !searchTerms.has('issue') || searchTerms.has('pr') || searchTerms.has('pull-request')
}

function removeDuplicateValues(values: Map<string, string[]>): Map<string, string[]> {
  const newQueryMap = new Map(values)
  const valuesForIs = newQueryMap.get('is') || []
  const valuesForType = newQueryMap.get('type') || []

  const hasTypeValue: Record<string, boolean> = {
    issue: valuesForIs.includes('issue'),
    pr: valuesForIs.includes('pr'),
  }

  if (!valuesForIs.length) {
    newQueryMap.delete('is')
  }

  // do not add type:pr or type:issue to the query if is:pr or is:issue is already present
  const newValues: string[] = valuesForType.filter(value => !hasTypeValue[value])

  if (newValues.length > 0) {
    newQueryMap.set('type', newValues)
  } else {
    newQueryMap.delete('type')
  }
  return newQueryMap
}

// For compatibility, remove qualifiers about repo, org, or user
// Also remove search terms (not filters) with user handles
// e.g., remove "@monalisa" but preserve "assignee:@monalisa"
function removeIgnoredQualifiers(queryString: string) {
  // Split the query string by space, but preserve label titles with spaces (within double quotes)
  const splitQueryString = queryString.match(/(".*?"|[^"\s]+)+/g) ?? []
  const ignoredQualifiersRegex = /^(repo:|org:|user:|@)/

  return splitQueryString.filter(v => !ignoredQualifiersRegex.test(v)).join(' ')
}

// Returns a map representing the key:value pairs present in map1 that are not in map2
function mapDifference(map1: Map<string, string[] | string>, map2: Map<string, string[] | string>) {
  const diff = new Map()
  for (const [key, val] of map1) {
    if (!map2.has(key)) {
      diff.set(key, val)
    }
  }
  return diff
}

export function isPrsOnly(activeSearchQuery: string) {
  const parsed = parseQuery(activeSearchQuery)
  const isData = parsed.get('is') || []
  const typeData = parsed.get('type') || []
  const combinedData = [...isData, ...typeData]
  const combinedDataMap = new Map(combinedData.map(str => [str, true]))
  const hasPrQualifier = combinedDataMap.has('pr') || combinedDataMap.has('pull-request')
  const hasIssueQualifier = combinedDataMap.has('issue') || combinedDataMap.has('issues')
  return hasPrQualifier && !hasIssueQualifier
}
